import style from "./style.scss";

import editorialStyle from "../../../components/editorial/style.scss";

import { mergeClasses } from "../../../components/helpers";
import Link, {
  Button,
  ButtonList,
  ButtonBar,
  StickyButtons,
  LargeViewOnly,
  ButtonDualGroup,
  ButtonGroup,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import Warning from "../../../components/warning";
import Accordion from "../../../components/accordion";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import { lookupByUrl } from "../../../settings/route-data-lookup";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { Fragment } from "preact";

import AffiliateAd from "../../../components/affiliate-ad";

import CompatibilityModule from "./compatibilityModule";

const headerInfo = lookupByUrl(namedRoutes.ecoflow.altCharger.review);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionY={51}
      background="/images/r/ecoflow-alternator-chargers/ecoflow-car-app.webp"
      titlePrefix="EcoFlow"
      title="Alternator Chargers"
      subTitle="500W and 800W Model Review"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          Extend your off-grid adventures with EcoFlow’s Alternator Chargers! I
          tested both the <strong>500W Alternator Charger</strong> and{" "}
          <strong>800W Alternator Charger</strong> models to see which is the
          best fit for your van, truck, or RV setup. EcoFlow’s Alternator
          Chargers are similar to DC-to-DC chargers, pulling energy from your
          vehicle’s alternator to keep your power station topped up while
          driving. When used with the XT150 cable, it also unlocks reverse
          charge and battery maintenance modes, adding even more flexibility for
          off-grid power solutions in vans, RVs, and overland setups.
        </p>

        <p>
          <strong>Short on time?</strong> Check out my{" "}
          <strong>
            <Link href={namedRoutes.ecoflow.altCharger.quick}>
              summary review
            </Link>
          </strong>{" "}
          (2 min read) instead of this detailed one.
        </p>

        <LargeViewOnly>
          <ButtonDualGroup small>
            <ButtonGroup title="800W Alternator Charger">
              <ButtonList size="medium">
                <Button
                  size="medium"
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/800w-alternator-charger"
                  icon="EcoFlow"
                >
                  View on EcoFlow
                </Button>
                <Button
                  size="medium"
                  affiliate="https://amzn.to/3N4b6p1"
                  href="https://www.amazon.com/dp/B0DSBZVPS6/"
                  secondary
                  icon="Amazon"
                >
                  View on Amazon
                </Button>
              </ButtonList>
            </ButtonGroup>
            <ButtonGroup title="500W Alternator Charger">
              <ButtonList size="medium">
                <Button
                  size="medium"
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger"
                  icon="EcoFlow"
                >
                  View on EcoFlow
                </Button>
                <Button
                  size="medium"
                  affiliate="https://amzn.to/4jzlwsP"
                  href="https://www.amazon.com/dp/B0FYCJ6KD3/"
                  secondary
                  icon="Amazon"
                >
                  View on Amazon
                </Button>
              </ButtonList>
            </ButtonGroup>
          </ButtonDualGroup>
        </LargeViewOnly>

        <AffiliateDisclosure />

        <ImageSet
          shadow={true}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_164631426.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_164631426.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_164631426.webp",
              alt: "800W vs 500W base units top view",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_164837918.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_164837918.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_164837918.webp",
              alt: "800W vs 500W base units side view",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_164245603.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_164245603.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_164245603.webp",
              alt: "XT150 cable vs XT60 cable",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_184511737.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_184511737.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_184511737.webp",
              alt: "Fuse blocks and 6AWG vs 8AWG cables",
            },
          ]}
        />

        <p>
          I started using the 800W model in summer 2024 on trips ranging from
          short getaways to month-long journeys. In April 2025, EcoFlow sent me
          the 500W model with XT60 cable. Here’s how the two models and cables
          compare.
        </p>

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Overview</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Key Differences</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Specifications</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Full Specifications</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Check Compatibility</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Output Cables</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Cable Observations</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Accessories</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Mobile App</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Switching Modes</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Charge Mode</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Reverse Charge Mode</SlugLink>
                        <ol class="lower-roman">
                          <li>
                            <SlugLink>Emergency Reverse Charging</SlugLink>
                          </li>
                        </ol>
                      </li>
                      <li>
                        <SlugLink>Maintenance Mode</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Starter Battery Settings</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Auto Timeout Setting</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Positive Electrode Extension Cable</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Attempted Dual 800W and 500W Chargers</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>
                          Why I Thought Dual Chargers Might Work
                        </SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Improved Fuse Block</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Fuse Type and Specification</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Feedback for EcoFlow</SlugLink>
                  </li>
                  <li>
                    <SlugLink>My Verdict</SlugLink>
                  </li>
                </ol>
              ),
            },
            {
              headingAs: "h3",
              heading: "My Setup / Test Configuration",
              content: (
                <Fragment>
                  <p>
                    All my tests were run on an{" "}
                    <Link
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-pro-portable-power-station?variant=40516140138569"
                    >
                      EcoFlow Delta Pro
                    </Link>{" "}
                    hooked up to a Ford Transit EcoBoost with a 250A alternator.
                    The cables supplied with the 800W model are connected to my
                    van’s dual batteries (without bypassing the BMS on the
                    negative side, per a conversation I had with a{" "}
                    <Link
                      external
                      href="https://www.fordpro.com/en-us/upfit/bbas/"
                    >
                      Ford BBAS
                    </Link>{" "}
                    Services Engineer). The cables supplied with the 500W model
                    are connected to CCP1 (Consumer Connection Point 1). CCP1
                    isn’t a good location to connect the 800W model, as CCP1 is
                    limited to 60A. Since the Delta Pro doesn’t have an XT150
                    port, tests requiring it were done with the{" "}
                    <Link
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter"
                    >
                      Smart Generator Adapter
                    </Link>
                    .
                  </p>
                  <ImageSet
                    shadow={true}
                    images={[
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20240720_221759375.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_221759375.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_221759375.webp",
                        alt: "Ford Transit dual batteries with EcoFlow alternator charger cables and Blue Sea fuse block",
                      },
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20240720_223337589.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_223337589.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_223337589.webp",
                        alt: "Ford Transit dual batteries with EcoFlow alternator charger cables",
                      },
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250427_185716860.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_185716860.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_185716860.webp",
                        alt: "Ford Transit CCP1 with EcoFlow 8AWG cable",
                      },
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250427_182938967.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_182938967.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_182938967.webp",
                        alt: "XT150 and XT60 connected to EcoFlow Delta Pro",
                      },
                    ]}
                  />
                  <p>
                    Currently I use my EcoFlow vanlife setup to power the
                    following items: induction cooktop (1800W), toaster (730W),{" "}
                    <Link
                      external
                      href="https://velitcamping.com/products/velit-gasoline-air-heater-17000btu"
                    >
                      VELIT gasoline heater
                    </Link>
                    ,{" "}
                    <Link
                      external
                      href="https://www.dometic.com/en/outdoor/coolers/electric-coolers/dometic-cfx3-75dz-225774"
                    >
                      Dometic CFX3 75DZ
                    </Link>{" "}
                    (fridge/freezer),{" "}
                    <Link
                      affiliate="https://amzn.to/47XhlBJ"
                      href="https://www.amazon.com/dp/B002OW5JG2"
                    >
                      Maxxfan Deluxe fans
                    </Link>{" "}
                    (x2) , laptop, phones/tablet, rechargeable lights,
                    rechargeable power banks, etc. I’ve even used it during a
                    power outage to run a full-size refrigerator and an
                    additional freezer, simply by running an extension cord from
                    my van into my house.
                  </p>
                  <ReviewItem href={namedRoutes.inventory.vanlife} />
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Overview</SlugHeading>
        <ul>
          <li>
            <strong>3-in-1 multifunctional design:</strong> In addition to
            charging, both models offer reverse charging and battery maintenance
            modes for the vehicle's battery when used with the XT150 or Custom
            Fit output cables.
          </li>
          <li>
            <strong>Compatible with many cars and devices:</strong> Also works
            to charge other brands of power stations (Bluetti, Jackery, etc.)
            when using the XT60 output cable.
          </li>
        </ul>
        <SlugHeading tag="h3">Key Differences</SlugHeading>
        <p>
          The main differences between the two models are the included cable and
          max power output:
        </p>
        <ul>
          <li>
            <strong>Output Cable:</strong> 800W includes XT150 cable, 500W
            includes XT60 cable.
          </li>
          <li>
            <strong>Features:</strong> Output cable changes what features are
            enabled and maximum power output.
          </li>
          <li>
            <strong>Compatibility:</strong> Both models are compatible with any
            of the three output cables.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Specifications</SlugHeading>
        <p>Detailed comparison of Gen 1 EcoFlow alternator chargers:</p>
        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                <th>800W</th>
                <th>500W</th>
              </tr>
            </thead>
            <tbody>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Photo</strong>
                </th>
                <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-800w/front-right.webp"
                    alt="EcoFlow Alternator Charger 800W"
                    showAlt
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-500w/front.webp"
                    alt="EcoFlow Alternator Charger 500W"
                    showAlt
                    maxHeight={100}
                  />
                </td>
              </tr>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Links</strong>
                </th>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/800w-alternator-charger?variant=41064248836169"
                      icon="EcoFlow"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://manuals.ecoflow.com/us/product/alternator-charger-800w"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger?variant=54348224954441"
                      icon="EcoFlow"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://manuals.ecoflow.com/eu/product/alternator-charger-500w"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Price</strong>
                </th>
                <td>
                  <NoWrap>
                    $269 <s>$599</s>
                  </NoWrap>
                </td>
                <td>
                  <NoWrap>
                    $199 <s>$389</s>
                  </NoWrap>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Cable Included</strong> <sup>2</sup>
                </th>
                <td>
                  <mark class={style.xt150}>
                    <strong>XT150</strong>
                  </mark>{" "}
                  <sup>1</sup>
                </td>
                <td>
                  <mark class={style.xt60}>
                    <strong>XT60</strong>
                  </mark>{" "}
                  <sup>1</sup>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Max Charge Rate</strong> <sup>3</sup>
                  <br />
                  <mark class={style.xt60}>XT60 cable</mark>
                </th>
                <td>500W</td>
                <td>
                  <mark class={style.xt60}>
                    <strong>500W</strong>
                  </mark>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Max Charge Rate</strong>
                  <br />
                  <mark class={style.xt150}>XT150 cable</mark>
                </th>
                <td>
                  <mark class={style.xt150}>
                    <strong>800W</strong>
                  </mark>
                </td>
                <td>500W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Max Reverse Charge</strong> <sup>3</sup>
                  <br />
                  <mark class={style.xt150}>XT150 cable</mark>
                </th>
                <td>
                  <mark class={style.xt150}>
                    <strong>800W</strong>
                  </mark>
                </td>
                <td>500W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Battery Maintenance</strong> <sup>3</sup>
                  <br />
                  <mark class={style.xt150}>XT150 cable</mark>
                </th>
                <td>
                  <mark class={style.xt150}>
                    <strong>100W</strong>
                  </mark>
                </td>
                <td>100W</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Size</strong>
                </th>
                <td>
                  9.5 × 7.6 × 1.4"
                  <br />
                  (242 × 194 × 35 mm)
                </td>
                <td>
                  9.5 × 7.6 × 1.4"
                  <br />
                  (242 × 194 × 35 mm)
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Weight</strong>
                </th>
                <td>
                  5.1 lbs
                  <br />
                  (2.3 kg)
                </td>
                <td>
                  3.5 lbs
                  <br />
                  (1.6 kg)
                </td>
              </tr>
            </tbody>
          </table>
        </FixedTable>
        <p class="caption">
          <strong>1.</strong> Bolded values are defaults with included cable.{" "}
          <strong>2.</strong> Custom Fit Output Cable offers same functionality
          and charge rates as XT150 output cable. <strong>3.</strong> Reverse
          Charge and Battery Maintenance modes aren't supported while using the
          XT60 output cable and the max charge rate could be lower depending on
          the power station.
        </p>

        <SlugHeading tag="h3">Full Specifications</SlugHeading>
        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "800W Alternator Charger",
              content: (
                <Fragment>
                  <h5>General</h5>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Model:</strong> EF-AC-001
                    </li>
                    <li>
                      <strong>Net Weight:</strong> 3.7 lbs (1.7 kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 10.9 × 7.4 × 1.5 in (276 ×
                      187 × 38 mm)
                    </li>
                    <li>
                      <strong>Included Output Cable:</strong> XT150
                    </li>
                    <li>
                      <strong>Input Cable AWG:</strong> 6AWG
                    </li>
                    <li>
                      <strong>Warranty period:</strong> 2 years
                    </li>
                  </ul>
                  <hr />
                  <h5>Power</h5>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      <strong>Charge Mode:</strong>
                      <ul>
                        <li>Input: 12V/24V⎓(11V-31V⎓), 76A Max.</li>
                        <li>Output: 40V-60V⎓, 800W Max.; 24V⎓, 360W Max.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Reverse Charge Mode:</strong>
                      <ul>
                        <li>Input: 40V-60V⎓, 21A Max.</li>
                        <li>Output: 13.8V/27.6V⎓, 800W Max.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Battery Maintenance Mode:</strong>
                      <ul>
                        <li>Input: 40V-60V⎓, 3A Max.</li>
                        <li>Output: 13.8V/27.6V⎓, 100W Max.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Solar Port Input:</strong> 48V⎓(11-60V⎓), 15A Max,
                      300W Max
                    </li>
                    <li>
                      <strong>Rated Current of Fuse:</strong> 125A
                    </li>
                    <li>
                      <strong>Protection Type:</strong> Reverse polarity
                      protection/Overcurrent protection/Short circuit
                      protection/Overvoltage and undervoltage protection
                      (Provided with integral protection against overloads)
                    </li>
                  </ul>
                  <hr />
                  <h5>Environmental Temperature</h5>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Operating Temperature:</strong> -4° to 140°F (-20°
                      to 60°C)
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> -22° to 158°F (-30°
                      to 70°C)
                    </li>
                    <li>
                      <strong>Operating Humidity :</strong> ≤95%
                    </li>
                    <li>
                      <strong>Storage Humidity :</strong> ≤95%
                    </li>
                  </ul>
                  <hr />
                  <h5>Communications</h5>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Wi-Fi:</strong>
                      <ul>
                        <li>Frequency: 2412MHz-2472MHz</li>
                        <li>Maximum output power: 18.22dBm</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Bluetooth:</strong>
                      <ul>
                        <li>Frequency: 2402MHz-2480MHz</li>
                        <li>Maximum output power: 7.89dBm</li>
                      </ul>
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "500W Alternator Charger",
              content: (
                <Fragment>
                  <h5>General</h5>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Model:</strong> EF-MS-H02-1
                    </li>
                    <li>
                      <strong>Net Weight:</strong> 3.5 lbs (1.6 kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 9.5 × 7.6 × 1.4" (242 × 194 ×
                      35 mm)
                    </li>
                    <li>
                      <strong>Included Output Cable:</strong> XT60
                    </li>
                    <li>
                      <strong>Input Cable AWG:</strong> 8AWG
                    </li>
                    <li>
                      <strong>Warranty period:</strong> 2 years
                    </li>
                  </ul>
                  <hr />
                  <h5>Power</h5>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      <strong>Charge Mode:</strong>
                      <ul>
                        <li>Input: 12V/24V⎓(11V-31V⎓), 45A Max.</li>
                        <li>Output: 40V-60V⎓, 500W Max.; 24V⎓, 360W Max.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Reverse Charge Mode:</strong>
                      <ul>
                        <li>Input: 40V-60V⎓, 13.2A Max.</li>
                        <li>Output: 13.8V/27.6V⎓, 500W Max.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Battery Maintenance Mode:</strong>
                      <ul>
                        <li>Input: 40V-60V⎓, 3A Max.</li>
                        <li>Output: 13.8V/27.6V⎓, 100W Max.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Solar Port Input:</strong> 48V⎓(11-60V⎓), 15A Max,
                      300W Max
                    </li>
                    <li>
                      <strong>Rated Current of Fuse:</strong> 125A
                    </li>
                    <li>
                      <strong>Protection Type:</strong> Reverse polarity
                      protection/Overcurrent protection/Short circuit
                      protection/Overvoltage and undervoltage protection
                      (Provided with integral protection against overloads)
                    </li>
                  </ul>
                  <hr />
                  <h5>Environmental Temperature</h5>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Operating Temperature:</strong> -4° to 140°F (-20°
                      to 60°C)
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> -22° to 158°F (-30°
                      to 70°C)
                    </li>
                    <li>
                      <strong>Operating Humidity :</strong> ≤95%
                    </li>
                    <li>
                      <strong>Storage Humidity :</strong> ≤95%
                    </li>
                  </ul>
                  <hr />
                  <h5>Communications</h5>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Wi-Fi:</strong>
                      <ul>
                        <li>Frequency: 2412MHz-2472MHz</li>
                        <li>Maximum output power: 18.22dBm</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Bluetooth:</strong>
                      <ul>
                        <li>Frequency: 2402MHz-2480MHz</li>
                        <li>Maximum output power: 7.89dBm</li>
                      </ul>
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "XT150 Output Cable",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Length:</strong>
                      <ul>
                        <li>800W model includes: 1m</li>
                        <li>Sold separately: 2m</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Supported Modes:</strong>
                      <ul>
                        <li>Charge</li>
                        <li>Reverse Charge</li>
                        <li>Battery Maintenance</li>
                      </ul>
                    </li>
                  </ul>
                  <hr />
                  <p class="caption" style="font-size:0.9em;">
                    The following power stations require the{" "}
                    <Link
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
                    >
                      Smart Generator Adapter
                    </Link>{" "}
                    when using this cable: <NoWrap>Delta Pro</NoWrap>,{" "}
                    <NoWrap>Delta Pro 3</NoWrap>,{" "}
                    <NoWrap>Delta 3 Max Plus</NoWrap>,{" "}
                    <NoWrap>Delta 3 Ultra Plus</NoWrap>. An alternative option
                    is to purchase the{" "}
                    <Link
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
                    >
                      Custom Fit Output Cable
                    </Link>{" "}
                    or XT60 output cable.
                  </p>
                </Fragment>
              ),
            },
            {
              heading: "XT60 Output Cable",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Length:</strong> 1m
                    </li>
                    <li>
                      <strong>Maximum Power:</strong> 500W <sup>1</sup>
                    </li>
                    <li>
                      <strong>Supported Mode:</strong> Charge only <sup>2</sup>
                    </li>
                  </ul>
                  <hr />
                  <p class="caption" style="font-size:0.9em;">
                    <strong>1.</strong> When using the XT60 output cable, the
                    charging speed may be limited by both the input capacity of
                    the power station and the XT60 cable itself.{" "}
                    <strong>2.</strong> When using this cable, the alternator
                    charger only supports Charge Mode.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />
        <p>
          EcoFlow recommends you keep the total electrical load of the vehicle,
          including all accessories and the alternator charger, below 50% of the
          alternator's rated capacity. EcoFlow states a typical alternator
          generates approximately twice the power required for a basic vehicle
          system while driving.
        </p>

        <AffiliateAd brand="EcoFlow" size="billboard" />

        <SlugHeading tag="h3">Check Compatibility</SlugHeading>
        <p>
          View my{" "}
          <Link href={namedRoutes.ecoflow.altCharger.gen1Compatibility}>
            detailed compatibility guide
          </Link>{" "}
          for confirmed EcoFlow power station support and 3rd-party battery
          system limits. My guide outlines the exact wattage limits for every
          combination of EcoFlow power station, alternator charger model, and
          output cable (XT60, XT150, or Custom Fit). It also includes a{" "}
          <strong>compatibility calculator</strong> for 3rd-party brands like
          BLUETTI, Jackery, and Anker to ensure your setup is safe and
          efficient.
        </p>
        <ReviewItem href={namedRoutes.ecoflow.altCharger.gen1Compatibility} />

        <p>
          <strong>Unsure which port or cable to use?</strong> My{" "}
          <Link href={namedRoutes.ecoflow.altCharger.gen1CableGuide}>
            cable connection guide
          </Link>{" "}
          includes the exact <strong>port types</strong> and{" "}
          <strong>port counts</strong> for every EcoFlow model, along with all
          cable options, adapters, and maximum charging rates.
        </p>
        <ReviewItem href={namedRoutes.ecoflow.altCharger.gen1CableGuide} />
      </Center>
      <Center>
        <SlugHeading tag="h2">Output Cables</SlugHeading>
        <p>
          Cable type determines system compatibility, available features, and
          maximum power output. Ideally you would be able to select which cable
          you want included when purchasing, but that's not the case.
        </p>

        <ul class={style.cables}>
          <li>
            <Image
              src="/images/products/ecoflow/alternator-charger-800w/output-cable-xt150-2m.webp"
              alt="XT150 Output Cable"
              showAlt
              class={mergeClasses([style.img, style.xt150])}
              buttonsData={[
                {
                  text: "EcoFlow",
                  icon: "EcoFlow",
                  affiliateData: affiliateMerchants.ecoflow,
                  href: "https://us.ecoflow.com/products/aecoflow-alternator-charger-xt150-output-cable-2m",
                },
              ]}
            />
            <div class={style.info}>
              <h3 class={style.productName}>
                <Link
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/aecoflow-alternator-charger-xt150-output-cable-2m"
                >
                  XT150 Output Cable
                </Link>
              </h3>
              <div>800W model includes (1m)</div>
              <div>Sold separately (2m)</div>
            </div>
            <div class={style.details}>
              <ul>
                <li>
                  <strong>Adjustable charge rate:</strong> 100-500W/100-800W
                  depending on alternator charger model.
                </li>
                <li>
                  <strong>Reverse charging:</strong> Charge your vehicle’s
                  battery from your EcoFlow in an emergency.
                </li>
                <li>
                  <strong>Battery maintenance mode:</strong> Keep your vehicle’s
                  battery healthy when stored.
                </li>
                <li>
                  <strong>Adapter:</strong> May require the{" "}
                  <Link
                    affiliateData={affiliateMerchants.ecoflow}
                    href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter"
                  >
                    Smart Generator Adapter
                  </Link>{" "}
                  to use with some power stations or purchase the{" "}
                  <Link
                    affiliateData={affiliateMerchants.ecoflow}
                    href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
                  >
                    Custom Fit Output Cable
                  </Link>{" "}
                  which offers the same features.
                </li>
              </ul>
            </div>
          </li>
          <li>
            <Image
              src="/images/products/ecoflow/alternator-charger-500w/output-cable-xt60i.webp"
              alt="XT60 Output Cable"
              showAlt
              class={mergeClasses([style.img, style.xt60])}
              buttonsData={[
                {
                  text: "EcoFlow",
                  icon: "EcoFlow",
                  affiliateData: affiliateMerchants.ecoflow,
                  href: "https://us.ecoflow.com/products/ecoflow-alternator-charger-xt60-output-cable-1m",
                },
              ]}
            />
            <div class={style.info}>
              <h3 class={style.productName}>
                <Link
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/ecoflow-alternator-charger-xt60-output-cable-1m"
                >
                  XT60 Output Cable
                </Link>
              </h3>
              <div>Included in: 500W model</div>
              <div>Length: 1m</div>
            </div>
            <div class={style.details}>
              <ul>
                <li>
                  <strong>Highly compatible:</strong> Even compatible with 3rd
                  party power stations and EcoFlow's River series.
                </li>
                <li>
                  <strong>Fixed 500W charge rate:</strong> No adjusting the
                  amperage. Voltage can be adjusted for 3rd-party devices.
                </li>
                <li>
                  <strong>No reverse charging.</strong>
                </li>
                <li>
                  <strong>No battery maintenance mode.</strong>
                </li>
              </ul>
            </div>
          </li>
          <li>
            <Image
              src="/images/products/ecoflow/accessories/output-cable-custom-fit-2m.webp"
              alt="Custom Fit Output Cable"
              showAlt
              class={style.img}
              buttonsData={[
                {
                  text: "EcoFlow",
                  icon: "EcoFlow",
                  affiliateData: affiliateMerchants.ecoflow,
                  href: "https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m",
                },
              ]}
            />
            <div class={style.info}>
              <h3 class={style.productName}>
                <Link
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
                >
                  Custom Fit Output Cable
                </Link>
              </h3>
              <div>Sold separately</div>
              <div>Length: 2m</div>
            </div>
            <div class={style.details}>
              <ul>
                <li>
                  <strong>Features:</strong> Gains access to same features and
                  charge rates as XT150 cable.
                </li>
                <li>
                  <strong>Compatibility:</strong> Delta Pro 3, Delta Pro, Delta
                  3 Ultra Plus, Delta 3 Max Plus.
                </li>
              </ul>
            </div>
          </li>
        </ul>

        <SlugHeading tag="h3">Cable Observations</SlugHeading>
        <p>
          The 500W cables are much more flexible and the XT60 cable also doesn’t
          have the large ferrite brick attached, making routing the cables
          easier than the ones that come with the 800W model.
        </p>
        <ImageSet
          shadow={true}
          showAlt
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_000205849.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_000205849.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_000205849.webp",
              alt: "XT150 vs XT60 output cable ends",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_000042495.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_000042495.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_000042495.webp",
              alt: "Alternator Charger Battery cable ends",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_000408082.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_000408082.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_000408082.webp",
              alt: "Alternator Charger Alt In cable ends",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250425_233552515.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_233552515.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_233552515.webp",
              alt: "8AWG vs 6AWG cable comparison",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250425_231648521.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_231648521.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_231648521.webp",
              alt: "EcoFlow 500W Alternator Charger cable package",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250425_232259472.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_232259472.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_232259472.webp",
              alt: "EcoFlow 500W Alternator Charger cables",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250425_232046660.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_232046660.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_232046660.webp",
              alt: "EcoFlow 500W Alternator Charger cable label",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250425_233501405.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_233501405.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_233501405.webp",
              alt: "Fuse block comparison",
            },
          ]}
        />
        <p>
          The 500W model works with both the supplied cables (8 AWG and XT60){" "}
          <strong>AND</strong> the larger gauge cables (6 AWG and XT150) that
          came with the original 800W model. The 800W model also works with the
          new XT60 cable (but should not be used with the 8 AWG cable due to
          fire risk). I tested a mix and match of both cable types on the 500W
          model.
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Accessories</SlugHeading>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
              img="/images/products/ecoflow/accessories/smart-generator-adapter.webp"
              title="Smart Generator Adapter"
              description={
                <Fragment>
                  Required by some power stations when using XT150 output cable
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-extra-battery-expansion-adapter?variant=55507615645769"
              img="/images/products/ecoflow/accessories/extra-battery-expansion-adapter.webp"
              title="Extra Battery Expansion Adapter"
              description={
                <Fragment>
                  Connects an alternator charger and extra battery for the DELTA
                  3 Max Plus and Ultra Plus
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-extended-fuse-cable-2m"
              img="/images/products/ecoflow/accessories/extended-fuse-cable-2m.webp"
              title="Extended Fuse Cable"
              description={<Fragment>Length: 2m</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Mobile App</SlugHeading>
        <p>
          The Alternator Charger requires connection to the EcoFlow app to
          enable operation and select working modes. It is available on{" "}
          <Link
            external
            href="https://apps.apple.com/us/app/ecoflow-power-a-new-world/id1506693140"
          >
            iOS
          </Link>{" "}
          and{" "}
          <Link
            external
            href="https://play.google.com/store/apps/details?id=com.ecoflow&hl=en_US"
          >
            Android
          </Link>
          , and supports CarPlay and Android Auto.
        </p>

        <SlugHeading tag="h3">Switching Modes</SlugHeading>
        <p>
          When connecting your EcoFlow portable power station using an XT150 or
          Custom Fit output cable, you can choose from three operating modes.
        </p>
        <ul>
          <li>Charge Mode</li>
          <li>Reverse Charge Mode</li>
          <li>Battery Maintenance Mode</li>
        </ul>

        <SlugHeading tag="h3">Charge Mode</SlugHeading>
        <p>
          Designed as the primary charging mode, this feature enables you to
          reliably charge your power station using your vehicle’s alternator.
          This ensures a full battery upon arrival at most destinations
          (depending on drive time), making it ideal for medium-to-long road
          trips where access to traditional power outlets (aka shore power) is
          limited. XT150 and Custom Fit output cables allow the charge rate to
          be adjusted, whereas the XT60 cable is fixed.
        </p>
        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_003715000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_003715000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_003715000.webp",
              alt: "Screenshot of 500W alternator charger with XT60 cable in charge mode in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_004243000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_004243000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_004243000.webp",
              alt: "Screenshot of 500W alternator charger with XT150 cable in charge mode in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_183400000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_183400000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_183400000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_183410000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_183410000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_183410000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_183417000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_183417000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_183417000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_203825000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_203825000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_203825000.webp",
              alt: "Screenshot of EcoFlow app",
            },
          ]}
        />

        <SlugHeading tag="h3">Reverse Charge Mode</SlugHeading>
        <p>
          Imagine being stranded with a dead vehicle battery and no cell
          service. Even if your power station is depleted, solar charging
          combined with the reverse charge feature allows you to recharge your
          vehicle and get back to safety. While I have never been in a situation
          where I had to rely on this feature, with the XT150 or Custom Fit
          output cables, it’s nice to know it’s there if I need it. This feature
          is not compatible with the XT60 output cable. It's also important to
          note that reverse charge is not the same as jump-starting a vehicle
          and will take a bit more time.
        </p>
        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_004330000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_004330000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_004330000.webp",
              alt: "Screenshot of 500W alternator charger with XT150 cable in reverse charge mode in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_004145000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_004145000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_004145000.webp",
              alt: "Screenshot of mode switch confirm popup in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_003946000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_003946000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_003946000.webp",
              alt: "Screenshot of XT150 cable feature popup in EcoFlow app",
            },

            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_004344000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_004344000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_004344000.webp",
              alt: "Screenshot of 500W alternator charger with XT150 cable adjusting reverse charge current in EcoFlow app",
            },
          ]}
        />
        <SlugHeading tag="h4">Emergency Reverse Charging</SlugHeading>
        <p>
          EcoFlow also added in a firmware update emergency reverse charging
          which is available on both the 500W and 800W models when using the
          XT150 or Custom Fit output cables. This allows the EcoFlow power
          station to recharge vehicle batteries when the voltage is too low for
          normal reverse charging. The charger provides a low current to the
          vehicle battery until it reaches the minimum voltage required for
          normal reverse charging. If the voltage remains below this level for
          30 minutes, charging will stop to protect your battery.
        </p>
        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_024209000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_024209000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_024209000.webp",
              alt: "Screenshot of 500W Alternator Charger device settings in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_182146000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_182146000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_182146000.webp",
              alt: "Screenshot explaining emergency reverse charge mode in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_182105000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_182105000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_182105000.webp",
              alt: "Screenshot of emergency reverse charge confirm popup in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_182111000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_182111000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_182111000.webp",
              alt: "Screenshot of 500W Alternator Charger device settings with emergency reverse charge enabled in EcoFlow app",
            },
          ]}
        />
        <SlugHeading tag="h3">Maintenance Mode</SlugHeading>
        <p>
          This mode delivers a steady 100W trickle charge to maintain your
          vehicle battery's health during long periods of inactivity. This
          feature only works with the XT150 and Custom Fit output cables and is
          not compatible with the XT60 output cable.
        </p>

        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_004458000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_004458000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_004458000.webp",
              alt: "Screenshot of 500W alternator charger with XT150 cable in maintenance mode in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_004145000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_004145000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_004145000.webp",
              alt: "Screenshot of XT150 cable feature popup in EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_003946000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_003946000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_003946000.webp",
              alt: "Screenshot of mode switch confirm popup in EcoFlow app",
            },
          ]}
        />

        <SlugHeading tag="h3">Starter Battery Settings</SlugHeading>
        <p>
          You can configure the system by selecting your vehicle’s battery type
          (12V or 24V) and choosing the connected power station (EcoFlow or
          third-party). In addition, it now allows you to set a protection level
          for the starter battery (low, mild, high).
        </p>
        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/7232.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/7232.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/7232.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_001942000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_001942000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_001942000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/7240.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/7240.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/7240.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/7241.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/7241.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/7241.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/7231.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/7231.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/7231.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/7216.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/7216.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/7216.webp",
              alt: "Screenshot of EcoFlow app",
            },
          ]}
        />

        <SlugHeading tag="h3">Auto Timeout Setting</SlugHeading>
        <p>
          With firmware version 3.0.4.13, you can now set a custom auto timeout
          duration (previously, the XT60 cable defaulted to a 30‑second
          timeout). This feature serves two key purposes: it prevents the
          alternator charger from shutting off while driving with a smart
          alternator, and it helps protect the starter batteries from excessive
          drain. I would like EcoFlow to add longer timeout options, such as 1
          day and 3 days, to better support overnight or weekend periods of
          inactivity.
        </p>
        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/7218.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/7218.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/7218.webp",
              alt: "Screenshot of EcoFlow app",
            },
          ]}
        />

        <SlugHeading tag="h3">Positive Electrode Extension Cable</SlugHeading>
        <p>
          It’s nice to see they added a new setting for "Positive electrode
          extension cable" which will help support more setups. Not sure if this
          means the negative electrode can't be extended or if the cables can't
          be shortened. Use your own cable or purchase EcoFlow's{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/ecoflow-alternator-charger-extended-fuse-cable-2m"
          >
            Extended Fuse Cable
          </Link>
          .
        </p>
        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_003707000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_003707000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_003707000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_003227000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_003227000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_003227000.webp",
              alt: "Screenshot of EcoFlow app",
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">
          Attempted Dual 800W and 500W Chargers
        </SlugHeading>
        <Image
          right
          shadow={true}
          small="/images/r/ecoflow-alternator-chargers/sm/20250427_151526775.webp"
          src="/images/r/ecoflow-alternator-chargers/lg/20250427_151526775.webp" // Back of Delta pro with both cables
          alt="XT150 and XT60 connected to EcoFlow Delta Pro"
        />
        <p>
          I was really hoping I could use both the 800W model AND the 500W model
          simultaneously on the same power station. This however does{" "}
          <strong>NOT</strong> work.
        </p>
        <p>
          It starts to look like it's working, but then the 500W model throws
          the following two errors, while the 800W model continues working:
        </p>
        <ul>
          <li>Power station port short circuit protection (64)</li>
          <li>Device error (16384)</li>
        </ul>
        <p>
          It was nice to see that EcoFlow designed these systems to protect
          themselves which should result in fewer product failures and warranty
          issues. Additionally, sometimes the app displays the details about the
          other alternator charger in the wrong view making it look like the
          500W model is getting 800W or the 800W model is getting 500W.
        </p>
        <ImageSet
          size={6}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_021817000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_021817000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_021817000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_023148000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_023148000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_023148000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_023152000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_023152000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_023152000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_022758000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_022758000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_022758000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_022029000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_022029000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_022029000.webp",
              alt: "Screenshot of EcoFlow app",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_183747000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_183747000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_183747000.webp",
              alt: "Screenshot of EcoFlow app",
            },
          ]}
        />
        <p>
          I had really hoped this would work as it would allow me to have access
          to 1,300W of charging power at any time (yes, my alternator can
          support this load, but I would only do it on rare occasions). Some
          vehicles come equipped with dual 250A alternators which would be more
          than enough power to use this scenario full-time.
        </p>
        <SlugHeading tag="h3">
          Why I Thought Dual Chargers Might Work
        </SlugHeading>
        <p>
          I’m able to achieve a similar input when the sky is clear and the sun
          is high as I have 600W of solar on my van’s roof.{" "}
          <strong>
            Dual charging with the Alternator Charger and Solar works amazing on
            my EcoFlow system!
          </strong>{" "}
          However, EcoFlow seems to have intentionally designed these systems to
          only allow for a single Alternator Charger product. I really hope they
          add support for multiple chargers in the future as there are vehicles
          with alternators which can support more than 800W… or perhaps they
          will consider releasing a more powerful model such as a 1600W!
        </p>
        <ImageSet
          size={4}
          shadow={true}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20240614_033417291.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20240614_033417291.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20240614_033417291.webp",
              alt: "x6 100W solar panels on roof of camper van",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_182354000.png",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_182354000.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_182354000.webp",
              alt: "Screenshot showing solar and alternator charger input in EcoFlow app",
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Improved Fuse Block</SlugHeading>
        <p>
          <strong>EcoFlow fixed the fuse block!!!</strong> They took mine and
          others' prior feedback and improved the inline fuse block. The old
          fuse block design was flawed; if too much strain was put on the cable
          it would cause the fuse to break which would result in an under
          voltage error and the inability to charge anything with the unit. I
          encountered this on my 800W model (I’m unsure which fuse block the
          current 800W model ships with currently as I preordered mine last year
          when it was announced).
        </p>
        <ImageSet
          size={4}
          shadow={true}
          showAlt
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250425_233516643.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_233516643.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_233516643.webp",
              alt: "EcoFlow fuse block comparison",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250426_015643895.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_015643895.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_015643895.webp",
              alt: "EcoFlow's new fuse block",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20240719_210422414.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20240719_210422414.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20240719_210422414.webp",
              alt: "EcoFlow vs Blue Sea Systems fuse block",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250427_145200253.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_145200253.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_145200253.webp",
              alt: "EcoFlow AEP fuses: new vs broken",
            }, // Broken fuse
          ]}
        />
        <p>
          To resolve my issue, I purchased a{" "}
          <Link
            affiliate="https://amzn.to/3LwA53B"
            href="https://www.amazon.com/dp/B004T1VYLA"
          >
            Blue Sea 7720 Safety Fuse Block
          </Link>{" "}
          which has separate posts for the fuse ends and the cables. The new
          design incorporates this separate post design. This helps prevent the
          issue where lateral strain on the cable would cause the fuse to shift
          and break.
        </p>
        <p>
          The Blue Sea design is still superior (but larger) because it uses
          screws to tighten the fuse and nuts to tighten the cables; this helps
          prevent unintentional over torquing of the fuse which can also cause
          the fuse to break (less torque can be applied through a Phillips head
          screw vs a nut). EcoFlow recommends 3.5-4.5 N⋅m of torque. Also,
          EcoFlow’s new design got rid of the transparent window which is used
          to quickly see if the fuse is blown without needing to remove the
          cover (this is small issue but can still be useful).
        </p>
        <p>
          I would love to see EcoFlow integrate both of these changes in the
          future (Phillips head screw for the fuse and a transparent
          window/cover), but overall this new fuse block is such a huge
          improvement. <strong>Awesome job EcoFlow!</strong>
        </p>

        <SlugHeading tag="h2">Fuse Type and Specification</SlugHeading>
        <Image
          right
          shadow={true}
          small="/images/r/ecoflow-alternator-chargers/sm/20250427_144646177.webp"
          src="/images/r/ecoflow-alternator-chargers/lg/20250427_144646177.webp" // Photo of fuse
          alt="EcoFlow AEP fuse 125A 65VDC"
        />
        <p>
          I would like to see EcoFlow sell the{" "}
          <Link external href="https://www.adlerelectric.com/product/AEP/">
            125A AEP fuse
          </Link>{" "}
          (model AEP3125301,{" "}
          <Link
            external
            href="https://pim.adlerelectric.com/var/file_storage/catalog/e/2/f/d/e2fda88566fe25e8834fa943def0e520e40720b9_AEP_M5_Bolt_65Vdc_EV_Fuse.pdf"
          >
            see data sheet
          </Link>
          ) on their online store (and Amazon) and also recommend suitable
          3rd-party replacements that are easy to obtain.{" "}
          <strong>
            If the fuse blows while someone is on the road, they need to be able
            to quickly source a new one that is commonly available.
          </strong>{" "}
          EcoFlow should clearly document the min/max requirements for the fuse
          (amps and volts) for each model instead of having customers try to
          reverse engineer something designed to prevent an electrical fire.
        </p>

        <Accordion
          headingsAs="h3"
          items={[
            {
              heading: "Advanced fuse information",
              content: (
                <Fragment>
                  <p>
                    The included AEP fuse is rated at 65V, however where it is
                    placed it should be subject to no more than 31V based on
                    EcoFlow’s specifications. Commonly available and similarly
                    sized fuses (AMI/MIDI) aren’t rated for 65V so it would be
                    very helpful if EcoFlow officially clarified this. If 32V
                    fuses are safe to use, then Blue Sea Systems has many
                    options available (such as{" "}
                    <Link
                      affiliate="https://amzn.to/47pSFm2"
                      href="https://www.amazon.com/dp/B004ZJ0V1U/"
                    >
                      125A
                    </Link>
                    ).
                  </p>
                  <p>
                    The EcoFlow fuse seems to be over spec’d (not in a good way)
                    based on the stats of the 500W model (and perhaps even the
                    800W model). It’s also strange that both models use the
                    exact same fuse to protect two different cable sizes: 6 AWG
                    vs 8 AWG. It feels like either they didn’t recalculate what
                    fuse was needed for the 500W model or the fuse is there only
                    to protect the EcoFlow device rather than to prevent an
                    actual electrical fire.
                  </p>

                  <SlugHeading tag="h3">Calculating Fuse Size</SlugHeading>

                  <Warning prefix="Warning">
                    Please seek professional advice if planning on using a fuse
                    different than what EcoFlow recommends! I’m not an
                    electrician or electrical engineer.
                  </Warning>

                  <p>
                    According to EcoFlow, the max amps is 76A for the 800W model
                    and 45A for the 500W model:
                  </p>
                  <FixedTable>
                    <table>
                      <thead>
                        <tr>
                          <th class={editorialStyle.sticky} />
                          <th>
                            800W cable
                            <br />
                            (6AWG)
                          </th>
                          <th>
                            500W cable
                            <br />
                            (8AWG)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th class={editorialStyle.sticky}>Max Amps</th>
                          <td>76A</td>
                          <td>45A</td>
                        </tr>
                        <tr>
                          <th class={editorialStyle.sticky}>Max Volts</th>
                          <td>31V</td>
                          <td>31V</td>
                        </tr>
                      </tbody>
                    </table>
                  </FixedTable>

                  <p>
                    Based on a{" "}
                    <Link
                      external
                      href="https://www.bluesea.com/support/articles/Circuit_Protection/1441/Part_2%3A_Select_a_Fuse_and_Fuse_Holder_For_Your_DC_Product_Installation"
                    >
                      guide from Blue Sea Systems
                    </Link>{" "}
                    and the specifications from EcoFlow, I calculated:
                  </p>
                  <FixedTable>
                    <table>
                      <thead>
                        <tr>
                          <th class={editorialStyle.sticky} />
                          <th>Location</th>
                          <th>
                            800W cable
                            <br />
                            (6AWG)
                          </th>
                          <th>
                            500W cable
                            <br />
                            (8AWG)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th class={editorialStyle.sticky}>
                            Min Fuse Rating <sup>1</sup>
                          </th>
                          <th>--</th>
                          <td>95A</td>
                          <td>56.3A</td>
                        </tr>
                        <tr class={editorialStyle.rowBorder}>
                          <th class={editorialStyle.sticky} rowSpan={2}>
                            Max Fuse Rating <sup>2</sup>
                          </th>
                          <th>Hot Engine Bay</th>
                          <td>100A</td>
                          <td>70A</td>
                        </tr>
                        <tr>
                          <th>Inside</th>
                          <td>125A</td>
                          <td>80A</td>
                        </tr>
                      </tbody>
                    </table>
                  </FixedTable>
                  <p class="caption">
                    <strong>1)</strong> Minimum amp rating equals max product
                    amperage x 125%.{" "}
                    <NoWrap>
                      <strong>2)</strong> Assumes
                    </NoWrap>{" "}
                    "single wire" for cable size.
                  </p>

                  <p>
                    Assuming AEP fuses are spec’d similar to AMI/MIDI fuses, it
                    appears the 800W model is fused at the complete upper limit
                    of its max and may be better suited with a 100A fuse (when
                    used in a hot engine compartment) and the 500W model is
                    fused way too high and should instead likely use a 70A fuse.
                  </p>

                  <p>
                    Based on my calculation, I understand these to be the
                    correct fuse options:
                  </p>
                  <ul>
                    <li>
                      <strong>800W model:</strong>{" "}
                      <Link
                        affiliate="https://amzn.to/40OlapM"
                        href="https://www.amazon.com/dp/B004ZJ0RTQ/"
                      >
                        100A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/47pSFm2"
                        href="https://www.amazon.com/dp/B004ZJ0V1U/"
                      >
                        125A
                      </Link>
                    </li>
                    <li>
                      <strong>500W model:</strong>{" "}
                      <Link
                        affiliate="https://amzn.to/47pSVl0"
                        href="https://www.amazon.com/dp/B004ZJ0R82/"
                      >
                        60A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/45zEOIK"
                        href="https://www.amazon.com/dp/B004ZJ0RL4/"
                      >
                        70A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/3Nb6RYQ"
                        href="https://www.amazon.com/dp/B004ZJ0S2M/"
                      >
                        80A
                      </Link>
                    </li>
                  </ul>
                  <p>
                    If you fuse too high you risk an electrical fire. If you
                    fuse too low you risk the fuse blowing during normal
                    operating conditions. When in doubt, fuse lower to prevent a
                    fire.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Feedback for EcoFlow</SlugHeading>
        <Accordion
          items={[
            {
              heading: "Feedback",
              content: (
                <Fragment>
                  <h3>Output Cable Options</h3>
                  <p>
                    Ideally when ordering a device, customers would have the
                    option to select which output cable type they would like
                    included. It feels like a waste to include a cable that a
                    customer potentially doesn’t need and then try to sell them
                    a separate cable for $79+.
                  </p>

                  <h3>Cable Lengths</h3>
                  <p>
                    With my vehicle’s 12V battery, the Alternator Charger boosts
                    the voltage to 48V. Since it’s more efficient to transfer
                    electricity over distances at a higher voltage (think
                    high-voltage transmission lines), I don’t understand why
                    EcoFlow has the longer cable carry 12/24V and the shorter
                    cable carry 48V. Swapping the cable lengths would allow the
                    Alternator Charger to be mounted in the front of a vehicle
                    making the on/off button potentially more accessible. I find
                    it strange the cable lengths aren’t swapped to increase
                    efficiency or at least provide an option for people who
                    would prefer this.
                  </p>

                  <h3>Maintenance Mode Charging Rate</h3>
                  <p>
                    Currently, the maintenance mode charging rate is fixed at
                    100W, which limits user control. Providing an adjustable
                    rate, similar to the reverse charge mode would be a welcome
                    addition. For comparison, the{" "}
                    <Link
                      affiliate="https://amzn.to/4qbNOvX"
                      href="https://www.amazon.com/dp/B07W3QSMF9/"
                    >
                      NOCO Genius 2D
                    </Link>{" "}
                    battery maintainer charges at 2A (28W max). I have had a
                    great experience with a NOCO Genius 2D I installed on a
                    different vehicle.
                  </p>

                  <h3>Start Voltage & Smart Alternators</h3>
                  <ImageSet
                    right
                    shadow={true}
                    images={[
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250426_004612000.png",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_004612000.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_004612000.webp",
                        alt: "Screenshot of car battery port undervoltage error in EcoFlow app",
                      },
                    ]}
                  />
                  <p>
                    New vehicles with smart alternators (to save fuel) can vary
                    their voltage output which makes determining the "Start
                    voltage" setting difficult. I have found myself on many
                    occasions needing to adjust this value down while driving to
                    trigger it to start charging, and then back up again while
                    off to have it stop charging. Some vehicles have a relay
                    built in that EcoFlow could make use of. Perhaps a small
                    device could be used and then either wirelessly connect or
                    connect via the RJ45 port on the charger.
                  </p>

                  <h3>Mounting Plate</h3>
                  <p>
                    It would be nice if EcoFlow added a second mounting option
                    with the included mounting plate along a short side of the
                    device instead of just the long side. While I personally
                    don’t require this, it would allow for additional
                    flexibility in some scenarios.
                  </p>
                  <ImageSet
                    shadow={true}
                    images={[
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250426_000912090.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_000912090.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_000912090.webp",
                        alt: "EcoFlow Alternator Charger mounting plate location",
                      },
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250426_000819541.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_000819541.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_000819541.webp",
                        alt: "EcoFlow Alternator Charger with mounting plate in wrong location",
                      },
                    ]}
                  />

                  <h3>Cable Remover</h3>
                  <p>
                    Another small improvement I would like to see EcoFlow make
                    is having the cables not require the proprietary cable
                    remover tool. Ideally they would incorporate a button design
                    similar to what is implemented on the{" "}
                    <Link
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter"
                    >
                      Smart Generator Adapter
                    </Link>{" "}
                    instead of requiring a special tool to keep track of. Or at
                    very least, provide a way for the tool to stow away onboard
                    the device so it doesn’t get misplaced.
                  </p>
                  <ImageSet
                    shadow={true}
                    images={[
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250427_151618476.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_151618476.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_151618476.webp",
                        alt: "EcoFlow Alternator Charger cable remover tool",
                      },
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250427_211024250.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_211024250.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_211024250.webp",
                        alt: "EcoFlow Alternator Charger cable remover tool in action",
                      },
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250427_151257122.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_151257122.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_151257122.webp",
                        alt: "EcoFlow Smart Generator Adapter and XT150 cable",
                      },
                      {
                        max: "/images/r/ecoflow-alternator-chargers/max/20250427_151202560.jpg",
                        lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_151202560.webp",
                        sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_151202560.webp",
                        alt: "EcoFlow Smart Generator Adapter and XT150 cable",
                      },
                    ]}
                  />
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">My Verdict</SlugHeading>
        <p>
          I can strongly recommend both models, but your choice depends heavily
          on the cable and your power station’s compatibility:
        </p>

        <ul>
          <li>
            <strong>
              If you have an unused XT150 port on your EcoFlow power station:
            </strong>{" "}
            Go with the 800W model for the extra power headroom and access to
            all features. The current price difference isn't too much ($199 vs
            $269) and you can limit the max charging of the device down as low
            as 100W if needed to support your vehicle’s needs (the XT60 cable
            cannot currently do this).
          </li>
          <li>
            <strong>If you need to use an XT60 port:</strong> Consider the 500W
            model but understand you’re missing out on some functionality.
          </li>
        </ul>

        <p>
          EcoFlow’s Alternator Chargers offer a compelling way to charge your
          power stations on the go.
        </p>

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/products/800w-alternator-charger"
          buttonText="Buy from EcoFlow"
        />

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers all the popular brands.
        </p>

        <ButtonDualGroup>
          <ButtonGroup title="800W Alternator Charger">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/800w-alternator-charger"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                affiliate="https://amzn.to/3N4b6p1"
                href="https://www.amazon.com/dp/B0DSBZVPS6/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
          <ButtonGroup title="500W Alternator Charger">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                affiliate="https://amzn.to/4jzlwsP"
                href="https://www.amazon.com/dp/B0FYCJ6KD3/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
        </ButtonDualGroup>

        <br />

        <p>
          <strong>Need a quick review?</strong> Read my{" "}
          <strong>
            <Link href={namedRoutes.ecoflow.altCharger.quick}>
              summary review
            </Link>
          </strong>{" "}
          (2 min read) instead of this detailed one.
        </p>
        <p>
          <strong>Need an install guide for a Ford Transit? </strong>Check out
          my{" "}
          <Link href={namedRoutes.ecoflow.altCharger.transit}>
            <strong>Ford Transit install guide</strong>
          </Link>
          .
        </p>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen1CableGuide} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.transit} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.wiring12v} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="EcoFlow Alternator Chargers"
        description="800W and 500W"
        options={[
          {
            text: "View on EcoFlow",
            icon: "EcoFlow",
            options: [
              {
                text: "800W Alternator Charger",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/800w-alternator-charger",
              },
              {
                text: "500W Alternator Charger",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/ecoflow-500w-alternator-charger",
              },
            ],
          },
          {
            text: "View on Amazon",
            icon: "Amazon",
            secondary: true,
            options: [
              {
                text: "800W Alternator Charger",
                affiliate: "https://amzn.to/3N4b6p1",
                href: "https://www.amazon.com/dp/B0DSBZVPS6/",
              },
              {
                text: "500W Alternator Charger",
                affiliate: "https://amzn.to/4jzlwsP",
                href: "https://www.amazon.com/dp/B0FYCJ6KD3/",
              },
            ],
          },
        ]}
      />
    </Editorial>
  </div>
);

export default Page;
